import React, { useState } from 'react';
import { Download, Loader, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../../ui/button';

const ImagesTab = ({ caseData }) => {
  const [downloading, setDownloading] = useState(false);
  
  const handleDownloadAll = async () => {
    if (!caseData?.id) return;
    
    setDownloading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cases/${caseData.id}/download-images`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) {
        throw new Error('Download fehlgeschlagen');
      }
      
      // Get filename from header or create default
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'bilder.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Download error:', error);
      alert('Download fehlgeschlagen');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadSingle = (img, idx) => {
    const imgUrl = typeof img === 'string' ? img : img.url;
    const imgName = typeof img === 'object' && img.slot_key ? img.slot_key : `bild_${idx + 1}`;
    
    // Open in new tab or download
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
    const a = document.createElement('a');
    a.href = fullUrl;
    a.download = `${imgName}.jpg`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (!caseData?.images || caseData.images.length === 0) {
    return (
      <div className="text-center py-12">
        <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">Keine Bilder hochgeladen</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with download all button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {caseData.images.length} Bilder
          </h3>
          <p className="text-sm text-gray-500">Klicken Sie auf ein Bild zum Vergrößern</p>
        </div>
        <Button
          onClick={handleDownloadAll}
          disabled={downloading}
          className="bg-brand-green hover:bg-brand-green-dark text-white"
          data-testid="download-all-images-btn"
        >
          {downloading ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Wird heruntergeladen...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Alle herunterladen (ZIP)
            </>
          )}
        </Button>
      </div>

      {/* Images grid */}
      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
        {caseData.images.map((img, idx) => (
          <div key={idx} className="group relative border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${typeof img === 'string' ? img : img.url}`}
              alt={`Bild ${idx + 1}`}
              className="w-full h-40 object-cover cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${typeof img === 'string' ? img : img.url}`, '_blank')}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadSingle(img, idx);
                }}
                className="bg-white/90 hover:bg-white text-gray-800 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Herunterladen
              </button>
            </div>
            <div className="p-3 bg-white border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-700 truncate">
                {typeof img === 'object' && img.slot_key ? img.slot_key : `Bild ${idx + 1}`}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImagesTab;
